import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Shop } from '../models/shop';
import { State } from '../redux/redux.state';
import client from '../clients/client';
import { createNewShop } from './new.shop.reducer';

const initialState: State<Shop[]> = { data: [], status: 'idle' };

// The list is about 1 MB, so it's fetched once per page load and shared by every
// view; after a failure, or once a new shop makes it stale, the next dispatch refetches.
export const getAllShops = createAsyncThunk<Shop[], void, { state: { shopsReducer: State<Shop[]> } }>(
  'shops/getAll',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await client.get('/shops');
      return data;
    } catch (error: any) {
      const { status, data } = error.response;
      return rejectWithValue({ status, errorMessage: data });
    }
  },
  {
    condition: (_, { getState }) => {
      const { status } = getState().shopsReducer;
      return status !== 'loading' && status !== 'successful';
    },
  }
);

export const getShopsByName = createAsyncThunk<Shop[], string>(
  'shops/getByName',
  async (shop_name: string, { rejectWithValue }) => {
    try {
      const { data } = await client.get(`/shops/${shop_name}`);
      return data;
    } catch (error: any) {
      const { status, data } = error.response;
      return rejectWithValue({ status, errorMessage: data });
    }
  }
);

const shopSlice = createSlice({
  name: 'shops',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getShopsByName.pending, (state, { payload }) => {
        state.status = 'loading';
      })
      .addCase(getShopsByName.fulfilled, (state, { payload }) => {
        state.status = 'successful';
        state.data = payload;
      })
      .addCase(getShopsByName.rejected, (state, action) => {
        state.status = 'error';
        state.errors = action.payload;
      })
      .addCase(getAllShops.pending, (state, { payload }) => {
        state.status = 'loading';
      })
      .addCase(getAllShops.fulfilled, (state, { payload }) => {
        state.status = 'successful';
        state.data = payload;
      })
      .addCase(getAllShops.rejected, (state, action) => {
        state.status = 'error';
        state.errors = action.payload;
      })
      .addCase(createNewShop.fulfilled, (state) => {
        state.status = 'idle';
      });
  },
});

export default shopSlice.reducer;
